/**
 * La fenêtre — une seule, pour les vingt dialogues de l'application.
 *
 * Chaque écran recopiait son `fixed inset-0 … bg-black/50`, et chaque copie
 * oubliait quelque chose : seize ne se fermaient pas sur Échap, aucune ne
 * bloquait le défilement derrière, aucune ne gardait le focus ni ne le rendait
 * au bouton qui l'avait ouverte, et toutes surgissaient d'un coup. Corriger
 * vingt copies une par une, c'est garantir la vingt et unième qui oublie.
 *
 * Ce qu'elle garantit :
 *
 *   · **Échap, le voile, la poignée** ferment — la fenêtre du dessus seulement,
 *     quand deux s'empilent (une commande, puis sa confirmation) ;
 *   · **le focus reste dedans** et revient, à la fermeture, là où il était ;
 *   · **la page derrière ne défile plus** ;
 *   · **elle sort par où elle est entrée** : du bas sur un téléphone, où
 *     c'est un panneau posé sous le pouce ; sur place sur un grand écran.
 *
 * Le mouvement est un ressort sans rebond, parti de la position affichée :
 * une fenêtre qu'on rattrape pendant qu'elle monte suit le doigt au lieu de
 * finir son animation. Sur téléphone, la poignée se tire vers le bas ; lâchée
 * avec élan, la fenêtre part avec la vitesse du doigt, et l'élan projeté —
 * pas seulement la position au lâcher — décide si elle se ferme.
 *
 * Fermer par le voile ne perd rien : les saisies longues ont leur brouillon
 * (`useFormDraft`), qui survit à la fermeture.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from "react";
// `m` + `LazyMotion` plutôt que `motion` : 27 ko compressés au lieu de 39 au
// premier chargement. `m` seul ne suffit pas — sans les fonctions chargées, il
// n'applique pas les valeurs animées, et la fenêtre reste hors de l'écran.
import {
  animate,
  domAnimation,
  LazyMotion,
  m,
  useMotionValue,
  useReducedMotion,
} from "framer-motion";
import { X } from "lucide-react";
import { retenirRecouvrement } from "@/lib/overlays";
import { cn } from "@/lib/utils";

interface Props {
  /** Appelée une fois la sortie jouée. C'est elle qui démonte la fenêtre. */
  onClose: () => void;
  children: ReactNode;
  /** Largeur, hauteur max, défilement : ce qui varie d'une fenêtre à l'autre. */
  className?: string;
  role?: "dialog" | "alertdialog";
  labelledBy?: string;
  describedBy?: string;
  label?: string;
  /**
   * Faux pendant un envoi : ni Échap ni le voile ne ferment une fenêtre dont
   * l'action est en cours.
   */
  dismissible?: boolean;
  /** `top` pour la palette, qui s'ouvre sous le regard plutôt qu'au centre. */
  placement?: "center" | "top";
  /** Au-dessus de la palette (z-60) quand elle en ouvre une. */
  zIndexClassName?: string;
}

const DialogContext = createContext<(() => void) | null>(null);

/**
 * Ferme la fenêtre qui contient l'appelant, sortie comprise. À préférer au
 * `onClose` du parent dans un bouton « Annuler » : celui-ci démonte sans
 * jouer la sortie.
 */
export function useDialogClose(): () => void {
  const close = useContext(DialogContext);
  if (!close) throw new Error("useDialogClose hors d'une <Dialog>");
  return close;
}

/** La croix, identique partout : même place, même taille, même libellé. */
export function DialogCloseButton({
  disabled,
  className,
}: {
  disabled?: boolean;
  className?: string;
}) {
  const close = useDialogClose();
  return (
    <button
      type="button"
      onClick={close}
      disabled={disabled}
      className={cn(
        "shrink-0 p-2 -m-1 rounded-lg text-text-muted hover:text-text-primary hover:bg-bg-input touch-target",
        className,
      )}
      aria-label="Fermer"
    >
      <X size={20} />
    </button>
  );
}

// ── La pile des fenêtres ouvertes ──────────────────────────────────────────
// Échap et la tabulation ne concernent que celle du dessus ; le défilement se
// bloque à la première et se libère à la dernière.
const pile: object[] = [];
let styleAvant: { overflow: string; paddingRight: string } | null = null;

function empiler(jeton: object) {
  if (pile.length === 0) {
    const body = document.body;
    styleAvant = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    // La barre de défilement disparaît avec `overflow: hidden` : sans cette
    // compensation, toute la page glisse de quelques pixels à l'ouverture.
    const barre = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (barre > 0) body.style.paddingRight = `${barre}px`;
  }
  pile.push(jeton);
}

function depiler(jeton: object) {
  const i = pile.indexOf(jeton);
  if (i >= 0) pile.splice(i, 1);
  if (pile.length === 0 && styleAvant) {
    document.body.style.overflow = styleAvant.overflow;
    document.body.style.paddingRight = styleAvant.paddingRight;
    styleAvant = null;
  }
}

const FOCUSABLES =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// ── Le mouvement ───────────────────────────────────────────────────────────
/** Ressort critique : pas de dépassement sur une fenêtre qui ne fait qu'apparaître. */
const RESSORT = { type: "spring", bounce: 0, duration: 0.35 } as const;
/** Le retour après un glisser relâché : l'élan venait du doigt, un léger rebond est juste. */
const RESSORT_RETOUR = { type: "spring", bounce: 0.2, duration: 0.3 } as const;

/**
 * Où l'élan emporterait la fenêtre si on la laissait filer — la décélération
 * d'un défilement. C'est ce point, et non celui du lâcher, qui décide.
 */
function projeter(vitesse: number, deceleration = 0.998): number {
  return ((vitesse / 1000) * deceleration) / (1 - deceleration);
}

/** Au-delà du bord, la fenêtre suit de moins en moins : elle résiste, sans se figer. */
function elastique(depassement: number, dimension: number, k = 0.55): number {
  return (depassement * dimension * k) / (dimension + k * Math.abs(depassement));
}

export function Dialog({
  onClose,
  children,
  className,
  role = "dialog",
  labelledBy,
  describedBy,
  label,
  dismissible = true,
  placement = "center",
  zIndexClassName = "z-50",
}: Props) {
  const reduit = useReducedMotion() ?? false;
  const panneau = useRef<HTMLDivElement>(null);
  const jeton = useRef({});
  const fermeture = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  // Lu une fois : une fenêtre ne change pas de nature en cours de route. Même
  // seuil que les classes `sm:` de la mise en page.
  const [panneauDuBas] = useState(
    () => typeof window !== "undefined" && !window.matchMedia("(min-width: 640px)").matches,
  );
  const glisse = panneauDuBas && !reduit;

  const voile = useMotionValue(0);
  const opacite = useMotionValue(glisse ? 1 : 0);
  const echelle = useMotionValue(reduit || panneauDuBas ? 1 : 0.96);
  // Hors écran avant la première image : le panneau ne doit pas clignoter en
  // place avant de monter.
  const y = useMotionValue(
    glisse ? (typeof window !== "undefined" ? window.innerHeight : 1000) : 0,
  );

  // L'entrée.
  useEffect(() => {
    const doux = { duration: 0.18, ease: "easeOut" } as const;
    const anims = [animate(voile, 1, { duration: 0.2, ease: "easeOut" })];
    if (glisse) anims.push(animate(y, 0, RESSORT));
    else if (reduit) anims.push(animate(opacite, 1, doux));
    else anims.push(animate(opacite, 1, doux), animate(echelle, 1, RESSORT));
    return () => anims.forEach((a) => a.stop());
  }, [glisse, reduit, voile, y, opacite, echelle]);

  const fermer = useCallback(
    (vitesse = 0) => {
      if (fermeture.current) return;
      fermeture.current = true;
      void animate(voile, 0, { duration: 0.2, ease: "easeIn" });
      let sortie;
      if (glisse) {
        const hauteur = panneau.current?.offsetHeight ?? window.innerHeight;
        // La sortie part à la vitesse du doigt : pas de couture entre le
        // glisser et l'animation.
        y.stop();
        sortie = animate(y, hauteur + 24, { ...RESSORT, duration: 0.3, velocity: vitesse });
      } else {
        animate(echelle, reduit ? 1 : 0.97, { duration: 0.15, ease: "easeIn" });
        sortie = animate(opacite, 0, { duration: 0.15, ease: "easeIn" });
      }
      void sortie.then(() => onCloseRef.current());
    },
    [glisse, reduit, voile, y, echelle, opacite],
  );

  // La pile, Échap, la tabulation, le focus.
  useEffect(() => {
    const moi = jeton.current;
    const avant = document.activeElement as HTMLElement | null;
    empiler(moi);
    const rendre = retenirRecouvrement();

    // Un champ `autoFocus` ou un composant qui pose son propre focus passe
    // avant ; sinon le focus entre dans la fenêtre, pour que Échap et Tab la
    // concernent.
    const el = panneau.current;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (pile[pile.length - 1] !== moi || !panneau.current) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        if (dismissibleRef.current) fermer();
        return;
      }
      if (e.key !== "Tab") return;
      const cibles = Array.from(panneau.current.querySelectorAll<HTMLElement>(FOCUSABLES)).filter(
        (c) => c.getClientRects().length > 0,
      );
      if (cibles.length === 0) {
        e.preventDefault();
        return;
      }
      const premier = cibles[0]!;
      const dernier = cibles[cibles.length - 1]!;
      const actif = document.activeElement;
      if (e.shiftKey && (actif === premier || actif === panneau.current)) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && actif === dernier) {
        e.preventDefault();
        premier.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      depiler(moi);
      rendre();
      if (avant?.isConnected) avant.focus({ preventScroll: true });
    };
  }, [fermer]);

  // Ce que reçoivent la croix et `useDialogClose` : une fermeture sans élan —
  // un gestionnaire de clic passerait son événement comme vitesse.
  const fermerSansElan = useCallback(() => fermer(), [fermer]);

  // Le voile ne ferme que si l'appui y a commencé : une sélection de texte
  // tirée hors du panneau ne doit pas fermer la fenêtre en relâchant.
  const appuiSurVoile = useRef(false);

  // ── La poignée ────────────────────────────────────────────────────────────
  const glisser = useRef<{ depart: number; releves: { y: number; t: number }[] } | null>(null);

  const saisir = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!glisse || fermeture.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    // Rattrapée en pleine montée, elle part d'où elle est affichée — et du
    // point où le doigt l'a prise, pas de son bord.
    y.stop();
    glisser.current = { depart: e.clientY - y.get(), releves: [{ y: y.get(), t: e.timeStamp }] };
  };

  const suivre = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = glisser.current;
    if (!g) return;
    const brut = e.clientY - g.depart;
    const hauteur = panneau.current?.offsetHeight ?? 400;
    y.set(brut >= 0 ? brut : elastique(brut, hauteur));
    g.releves.push({ y: y.get(), t: e.timeStamp });
    if (g.releves.length > 5) g.releves.shift();
  };

  const lacher = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = glisser.current;
    if (!g) return;
    glisser.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    // La vitesse du doigt *au lâcher* : les relevés de plus de 100 ms ne
    // comptent plus. Un doigt qui glisse, s'arrête, puis se lève n'a pas d'élan
    // — sans ce filtre, la vitesse d'avant la pause fermait la fenêtre.
    const recents = g.releves.filter((r) => e.timeStamp - r.t <= 100);
    const a = recents[0];
    const b = recents[recents.length - 1];
    const vitesse = a && b && b.t > a.t ? ((b.y - a.y) / (b.t - a.t)) * 1000 : 0;
    const hauteur = panneau.current?.offsetHeight ?? 400;
    const arrivee = y.get() + projeter(vitesse);
    if (dismissibleRef.current && arrivee > hauteur / 2) fermer(vitesse);
    else animate(y, 0, { ...RESSORT_RETOUR, velocity: vitesse });
  };

  return (
    <DialogContext.Provider value={fermerSansElan}>
      <LazyMotion features={domAnimation}>
        <div
          className={cn(
            "fixed inset-0 flex justify-center p-0",
            placement === "top"
              ? "items-end sm:items-start sm:pt-[12vh]"
              : "items-end sm:items-center sm:p-4",
            zIndexClassName,
          )}
          onPointerDown={(e) => {
            appuiSurVoile.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && appuiSurVoile.current && dismissibleRef.current) {
              fermer();
            }
          }}
        >
          <m.div
            aria-hidden
            className="absolute inset-0 bg-black/50 pointer-events-none"
            style={{ opacity: voile }}
          />
          <m.div
            ref={panneau}
            role={role}
            aria-modal="true"
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            aria-label={label}
            tabIndex={-1}
            style={{ y, opacity: opacite, scale: echelle }}
            className={cn(
              "relative w-full sm:max-w-md bg-bg-card border border-border sm:rounded-2xl rounded-t-2xl p-5 shadow-xl outline-none",
              className,
              // Après `className` : la place de la poignée survit à un `p-0`, et
              // le bas du panneau reste au-dessus de la barre d'accueil d'un iPhone.
              glisse && "pt-7 pb-[calc(1.25rem+env(safe-area-inset-bottom))]",
            )}
          >
            {glisse && (
              <div
                aria-hidden
                onPointerDown={saisir}
                onPointerMove={suivre}
                onPointerUp={lacher}
                onPointerCancel={lacher}
                className="absolute top-0 inset-x-0 h-7 flex items-center justify-center cursor-grab touch-none"
              >
                <span className="w-10 h-1 rounded-full bg-border-strong" />
              </div>
            )}
            {children}
          </m.div>
        </div>
      </LazyMotion>
    </DialogContext.Provider>
  );
}
